import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import Icon, { ArrowLeftIcon, ChevronRightIcon, XIcon } from "../common/icons";
import AssessmentIcon from "../common/AssessmentIcon";

// Choosing a test, then confirming it, in one dialog: the confirm step slides in
// where the list was instead of a second dialog opening on top. A bottom sheet on
// phones. Styles: "Test picker" in styles/chatbot.css (ap- classes).

// The names students see across the chat and the Results page; the test's own name small beside it
const TESTS = {
  riasec: {
    title: "Career interests",
    code: "RIASEC",
    description: ["What you enjoy, and the college programs that fit you", "Ang mga gusto mo, at ang mga programang bagay sa iyo"],
    questions: 42,
  },
  dass42: {
    title: "Mood and stress",
    code: "DASS-42",
    description: ["How you've been feeling over the past week", "Kumusta ang pakiramdam mo nitong nakaraang linggo"],
    questions: 42,
  },
  ryff: {
    title: "Well-being",
    code: "Ryff scale",
    description: ["How you see yourself and your life right now", "Kung paano mo nakikita ang sarili mo at ang buhay mo ngayon"],
    questions: 42,
  },
  sdq: {
    title: "Strengths and difficulties",
    code: "SDQ",
    description: ["Your feelings, how you act, and how you get along with others", "Ang iyong damdamin, kilos, at pakikitungo sa iba"],
    questions: 25,
  },
};

/** Starts left for a test, from the user record or the copy kept in localStorage; null when unknown. */
export const startsLeft = (user, type) => {
  let maxStarts = user?.max_chatbot_starts;
  if (maxStarts === undefined || maxStarts === null) {
    try {
      const raw = localStorage.getItem("max_chatbot_starts");
      maxStarts = raw ? JSON.parse(raw) : null;
    } catch {
      maxStarts = null;
    }
  }
  if (maxStarts === null || maxStarts === undefined) return null;
  if (typeof maxStarts === "string") {
    try {
      maxStarts = JSON.parse(maxStarts);
    } catch {
      maxStarts = Number(maxStarts);
    }
  }
  if (typeof maxStarts === "object") return maxStarts && maxStarts[type] != null ? Number(maxStarts[type]) : 0;
  if (typeof maxStarts === "number" && !Number.isNaN(maxStarts)) return maxStarts;
  return null;
};

const AssessmentPicker = ({
  show,
  language,
  availableAssessments,
  deadlineMessages,
  deadlineChecking,
  // True while the tests, deadlines and progress behind the list are still loading
  loading = false,
  user,
  canClose,
  onClose,
  onStart,
  // Opens the Results page for a finished test (the row says where the result went)
  onResults = null,
  // Unfinished tests: { dass42: { current: 12, total: 42 } } (current = answered so far)
  progress = {},
}) => {
  const fil = language === "fil";
  const t = (en, tl) => (fil ? tl : en);
  const [selected, setSelected] = useState(null);
  const [direction, setDirection] = useState("forward");

  useEffect(() => {
    if (show) setSelected(null);
  }, [show]);

  const testDeadlines = Object.keys(deadlineMessages || {}).filter((key) => key !== "_sessionLimit");
  const allPastDeadline = testDeadlines.length > 0 && testDeadlines.length === availableAssessments.length;
  const info = selected ? TESTS[selected] || TESTS.riasec : null;

  const choose = (type) => {
    setDirection("forward");
    setSelected(type);
  };
  const back = () => {
    setDirection("back");
    setSelected(null);
  };

  const renderTest = (type) => {
    const test = TESTS[type] || TESTS.riasec;
    const pastDeadline = deadlineMessages?.[type];
    const left = startsLeft(user, type);
    const used = left !== null && left <= 0;
    const resume = progress[type];
    const disabled = !resume && Boolean(pastDeadline || used);
    return (
      <li key={type}>
        <button type="button" className="ap-test" disabled={disabled} onClick={() => choose(type)}>
          <span className="ap-tile">
            <AssessmentIcon type={type} size={24} />
          </span>
          <span className="ap-test-text">
            <span className="ap-test-title">
              {test.title}
              <small className="ap-test-code">{test.code}</small>
            </span>
            <span className="ap-test-desc">{test.description[fil ? 1 : 0]}</span>
            {resume ? (
              <span className="ap-note is-progress">
                <Icon name="play" size={15} />
                {t(`Continue · question ${Math.min(resume.current + 1, resume.total)} of ${resume.total}`, `Ituloy · tanong ${Math.min(resume.current + 1, resume.total)} sa ${resume.total}`)}
              </span>
            ) : pastDeadline ? (
              <span className="ap-note is-danger">
                <Icon name="calendar-x" size={15} />
                {pastDeadline}
              </span>
            ) : used ? (
              <span className="ap-note is-done">
                <Icon name="check-circle" size={15} />
                {type === "riasec"
                  ? t("Done · Your result and programs are on your Results page", "Tapos na · Nasa Results page mo ang resulta at mga programa")
                  : t("Done · Your result is on your Results page", "Tapos na · Nasa Results page mo ang resulta")}
              </span>
            ) : (
              <span className="ap-meta">
                <span><Icon name="list" size={15} />{test.questions} {t("questions", "tanong")}</span>
              </span>
            )}
          </span>
          {!disabled && <ChevronRightIcon className="ap-chevron" size={20} />}
        </button>
        {used && !resume && onResults && (
          <button type="button" className="ap-link" onClick={() => onResults(type)}>
            {t("Open my Results page", "Buksan ang Results page ko")}
            <ChevronRightIcon size={16} />
          </button>
        )}
      </li>
    );
  };

  return (
    <Modal
      show={show}
      onHide={canClose ? onClose : () => {}}
      centered
      backdrop={canClose ? true : "static"}
      keyboard={canClose}
      className="ap-root"
      dialogClassName="ap-dialog"
      contentClassName="ap-content"
      aria-labelledby="ap-title"
    >
      <div className="ap-head">
        {info && (
          <button type="button" className="ap-icon-btn" onClick={back} aria-label={t("Back", "Bumalik")}>
            <ArrowLeftIcon size={20} />
          </button>
        )}
        <div className="ap-head-text">
          <h2 className="ap-title" id="ap-title">
            {info
              ? progress[selected]
                ? t("Pick up where you left off?", "Ituloy kung saan ka huminto?")
                : t("Ready to start?", "Handa ka na ba?")
              : t("Choose a test", "Piliin ang test")}
          </h2>
          <p className="ap-sub">
            {info
              ? progress[selected]
                ? t("Your answers so far are saved.", "Naka-save ang mga sagot mo.")
                : t("Check this is the test you want.", "Siguraduhing ito ang test na gusto mo.")
              : t("Pick one to start, or carry on with one you've started.", "Pumili ng sisimulan, o ituloy ang nasimulan mo na.")}
          </p>
        </div>
        {canClose && !info && (
          <button type="button" className="ap-icon-btn" onClick={onClose} aria-label={t("Close", "Isara")}>
            <XIcon size={20} />
          </button>
        )}
      </div>

      <div className="ap-body">
        <div key={selected || "list"} className={`ap-pane is-${direction}`}>
          {info ? (
            <div className="ap-confirm">
              <span className="ap-tile is-large">
                <AssessmentIcon type={selected} size={34} />
              </span>
              <h3 className="ap-confirm-title">{info.title}</h3>
              <p className="ap-confirm-desc">{info.description[fil ? 1 : 0]}</p>
              <div className="ap-chips">
                {progress[selected] ? (
                  <span><Icon name="play" size={16} />{t(`Question ${Math.min(progress[selected].current + 1, progress[selected].total)} of ${progress[selected].total}`, `Tanong ${Math.min(progress[selected].current + 1, progress[selected].total)} sa ${progress[selected].total}`)}</span>
                ) : (
                  <span><Icon name="list" size={16} />{info.questions} {t("questions", "tanong")}</span>
                )}
              </div>
              <p className="ap-callout">
                <Icon name="info" size={18} />
                {t(
                  "You can stop anytime. Your answers are saved after each one, so you can pick up later.",
                  "Puwede kang tumigil anumang oras. Naka-save ang bawat sagot, kaya puwede mong ituloy mamaya.",
                )}
              </p>
            </div>
          ) : (
            <>
              {!loading && deadlineMessages?._sessionLimit && (
                <p className="ap-alert">
                  <Icon name="alert-triangle" size={18} />
                  <span>
                    <b>{t("Session Limit Reached", "Session Limit Naabot na")}</b>
                    {deadlineMessages._sessionLimit}
                  </span>
                </p>
              )}
              {!loading && allPastDeadline && (
                <p className="ap-alert is-danger">
                  <Icon name="alert-triangle" size={18} />
                  <span>
                    <b>{t("All Deadlines Have Passed", "Lahat ng Deadline ay Lumipas na")}</b>
                    {t(
                      "The deadline for all available assessments has passed. Please contact the guidance office for further assistance.",
                      "Ang deadline para sa lahat ng available na assessments ay lumipas na. Pakikontak ang guidance office para sa karagdagang tulong.",
                    )}
                  </span>
                </p>
              )}
              {loading || deadlineChecking ? (
                // Opens at once; placeholder cards hold the space while the list loads
                <ul className="ap-list is-loading" aria-busy="true" aria-label={t("Loading your tests", "Kinukuha ang iyong mga test")}>
                  {Array.from({ length: Math.min(Math.max(availableAssessments.length, 2), 4) }, (_, i) => (
                    <li key={i}>
                      <span className="ap-test is-skeleton" aria-hidden="true">
                        <span className="ap-tile" />
                        <span className="ap-test-text">
                          <i className="ap-skel is-title" />
                          <i className="ap-skel is-line" />
                          <i className="ap-skel is-short" />
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              ) : availableAssessments.length > 0 ? (
                <ul className="ap-list">{availableAssessments.map(renderTest)}</ul>
              ) : (
                <div className="ap-state">
                  <b>{t("No Assessments Available", "Walang Available na Tests")}</b>
                  {t("No assessments available at the moment.", "Walang available na tests sa ngayon.")}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {info ? (
        <div className="ap-foot">
          <button type="button" className="ap-btn is-ghost" onClick={back}>
            {t("Go Back", "Bumalik")}
          </button>
          <button type="button" className="ap-btn is-primary" onClick={() => onStart(selected)}>
            {progress[selected] ? t("Continue", "Ituloy") : t("Start test", "Simulan")}
            <Icon name="arrow-right" size={18} />
          </button>
        </div>
      ) : (
        !loading &&
        !deadlineChecking &&
        availableAssessments.length === 0 &&
        canClose && (
          <div className="ap-foot">
            <button type="button" className="ap-btn is-ghost" onClick={onClose}>
              {t("Close", "Bumalik")}
            </button>
          </div>
        )
      )}
    </Modal>
  );
};

export default AssessmentPicker;
